import {SOUNDS} from "./sounds";
import {CITY_FOUNDED_SOUND_ID} from "../configs/buildingConstructionAnimation";
import {resolveMeleeStrikeSoundTier} from "../configs/meleeAnimation";
import UNIT_TYPES from "../configs/units";
import {isEnvConfEnabled} from "../utils/env/isEnvConfEnabled";
import {ENV_CONFIG_VARIABLES} from "../constants/env";
import {COMMON_BUILDING_TOKENS, KEY_POINT_TOKENS, SPECIAL_BUILDING_TOKENS} from "../constants/tokens";
import {checkIfCentralQuarter} from "../utils/buildings/checkIfCentralQuarter";
import {checkIfAnyBuildingToken} from "../utils/buildings/checkIfAnyBuildingToken";
import {getRandomElement} from "../utils/other/getRandomElement";
import {checkIfSpecialBuilding} from "../utils/buildings/checkIfSpecialBuilding";
import {MINE_EXPLORATION} from "../configs/factions/dwarves";
import {INITIAL_VOLUME, LOCAL_STORAGE_KEY_DEMO_VOLUME, LOCAL_STORAGE_KEY_VOLUME} from "../constants/sound";
import {MENU_SOUND_MANAGER} from "./MenuSoundManager";
import {FACTIONS} from "../constants/factions";
import {NEUTRAL_PLAYER_ID} from "../constants/players";
import {FACTIONS_WITH_UNIT_SOUNDS_WITHOUT_SKIN, checkIfUnitSoundsWithoutSkin} from "../configs/factionUnitSounds";
import {checkIfSoundOutputAsleep} from "../utils/sound/checkIfSoundOutputAsleep";
import {resolveFactionUnitSoundId} from "../utils/sound/resolveFactionUnitSoundId";
import {GOBLINS_BOMB_CAST, resolveGoblinsSuccessSoundId, resolveLeprechaunsTheftLaughSoundId} from "../configs/abilityCastAnimation";
import {resolveUnitRecruitmentCast} from "../configs/unitRecruitmentAnimation";
import {checkIfUnitHasFactionSkin} from "../utils/factions/checkIfUnitHasFactionSkin";
import {selectGameType, selectPlayers} from "../redux/reducers/sharedStateReducer/selectors";
import {GAME_TYPES} from "../constants/game";
import {getActingPlayerFromState} from "../utils/players/getActingPlayer";
import {
  DEFERRED_SOUNDS,
  DEFERRED_SOUNDS_LOADING,
  checkIfDeferredSoundNeeded,
  getDeferredSound,
} from "../configs/deferredSounds";

// Ретраи загрузки отдельного звука после ошибки (см. _handleFileLoadError):
// не проблема самой игры, а обход застрявшего в кеше браузера некорректного
// ответа на конкретный урл — уникальный query-параметр гарантированно не
// совпадает ни с одной записью HTTP-кеша, так что браузер обязан сходить в
// сеть заново, а не молча переиспользовать то, что уже лежит закешированным
const MAX_SOUND_LOAD_RETRIES = 3;
const SOUND_LOAD_RETRY_BASE_DELAY_MS = 1200;

// Задержка между звуками нескольких ловушек Гоблинов, сработавших за одно
// перемещение (см. playGoblinsTrapTrigger) - без неё несколько срабатываний
// подряд/одновременно слились бы в один неразличимый звук
const TRAP_TRIGGER_SOUND_STAGGER_MS = 350;
// Грохот разрушения идет со вторым взрывом на поле (см. GOBLINS_BOMB_CAST.destroyed),
// смех гоблина — когда взрыв уже отгремел и его слышно
const GOBLINS_DESTROYED_SECOND_BLAST_DELAY_MS = GOBLINS_BOMB_CAST.destroyed.secondBlastDelayMs;
const GOBLINS_LAUGH_AFTER_EXPLOSION_DELAY_MS = 700;

// Запас, после которого отложенный звук считается протухшим (см. _delaySound).
// Обычная задержка кадра и лаги в него укладываются, а часы, простоявшие в
// фоновой вкладке, — нет
const STALE_DELAYED_SOUND_TOLERANCE_MS = 1500;

export default class SoundManager {
  constructor(gameScene) {
    this.gameScene = gameScene;

    this.soundCache = new Map();
    this.soundsLoaded = false;
    this.soundsLoadRequested = false;
    this.soundLoadRetryCountById = new Map();
    this.soundLoadRetryTimeoutIds = [];
    // Отложенные звуки (см. _scheduleDeferredSoundsLoad)
    this.deferredSoundsLoadRequested = false;
    this.deferredSoundsTimeoutId = null;
    this.deferredSoundsAbort = null;
    this.isShutDown = false;
  }

  preload() {
    // Демо-партия в главном меню по умолчанию без звука (громкость 0) — не
    // тратим трафик на ~32МБ звуков, пока пользователь явно не поднимет
    // громкость слайдером (см. MenuDemoGame/ensureSoundsLoaded); обычные
    // игры грузят звуки сразу, как и раньше
    if (this.gameScene.isDemoMode && this._getGlobalVolume() === 0) {
      return;
    }
    this._loadAllSounds();
  }

  // Догрузка звуков вне обычного цикла preload() — когда пользователь
  // включает звук демо-партии уже после того, как загрузка сцены
  // отработала (и preload() выше пропустил звуки). Идемпотентно: безопасно
  // звать повторно, в т.ч. если звуки уже загружены/грузятся
  ensureSoundsLoaded() {
    if (this.soundsLoaded || this.soundsLoadRequested) {
      return;
    }
    this._loadAllSounds();
    this.gameScene.load.start();
  }

  _loadAllSounds() {
    if (this.soundsLoaded || this.soundsLoadRequested) {
      return;
    }
    this.soundsLoadRequested = true;
    Object.keys(SOUNDS).forEach(key => {
      this._preloadSound(key);
    });
    // 'complete' срабатывает по опустошению очереди загрузки независимо от
    // того, сколько отдельных файлов упало с ошибкой (Phaser.Loader.Events.
    // FILE_LOAD_ERROR идет по каждому такому файлу отдельно, не отменяя
    // общего complete) — поэтому ошибки одних звуков не блокируют флаг
    // готовности остальных, а неудачные подхватываются отдельным ретраем ниже
    this.gameScene.load.once('complete', () => {
      this.soundsLoaded = true;
      // Основные ассеты загружены — теперь, в фоне, можно и необязательные звуки
      this._scheduleDeferredSoundsLoad();
    });
    this.gameScene.load.on('loaderror', this._handleFileLoadError, this);
  }

  shutdown() {
    try {
      this.isShutDown = true;
      clearTimeout(this.deferredSoundsTimeoutId);
      this.deferredSoundsTimeoutId = null;
      this.deferredSoundsAbort?.abort?.();
      this.deferredSoundsAbort = null;
      this.soundLoadRetryTimeoutIds.forEach(clearTimeout);
      this.soundLoadRetryTimeoutIds = [];
      this.gameScene.load.off('loaderror', this._handleFileLoadError, this);
      this._clearSoundCache();
    } catch (error) {
      console.log(`[SoundManager.shutdown] error: ${error}`);
    }
  }

  _preloadSound(soundId, cacheBustQuery = '') {
    try {
      this.gameScene.load.audio(soundId, `/sounds/${soundId}.mp3${cacheBustQuery}`);
    } catch (error) {
      console.log(`[SoundManager.preloadSound] error: ${error}`);
    }
  }

  // Ошибка загрузки отдельного файла (см. константы вверху файла) — реагируем
  // только на свои звуки (та же LoaderPlugin у сцены могла грузить и другие
  // ресурсы, например картинки). До исчерпания лимита — переретраить с
  // уникальным query-параметром; после — тихо смириться, как и раньше
  // (_getCachedSound поймает исключение Phaser'а "not found in cache" сама)
  _handleFileLoadError(file) {
    try {
      const soundId = file?.key;
      if (file?.type !== 'audio' || !soundId || !(soundId in SOUNDS)) {
        return;
      }
      const attempt = (this.soundLoadRetryCountById.get(soundId) || 0) + 1;
      if (attempt > MAX_SOUND_LOAD_RETRIES) {
        console.log(`[SoundManager] "${soundId}" failed to load after ${MAX_SOUND_LOAD_RETRIES} retries — giving up`);
        return;
      }
      this.soundLoadRetryCountById.set(soundId, attempt);
      this._scheduleSoundRetry(soundId, attempt);
    } catch (error) {
      console.log(`[SoundManager.handleFileLoadError] error: ${error}`);
    }
  }

  _scheduleSoundRetry(soundId, attempt) {
    const timeoutId = setTimeout(() => {
      this._startSoundRetryWhenLoaderReady(soundId, attempt);
    }, SOUND_LOAD_RETRY_BASE_DELAY_MS * attempt);
    this.soundLoadRetryTimeoutIds.push(timeoutId);
  }

  // load.start() требует состояния LOADER_IDLE/LOADER_COMPLETE — если сцена
  // еще догружает другие файлы этой же (не звуковой) очереди, ждем и
  // проверяем снова, а не падаем в Phaser'овское исключение о неверном состоянии
  _startSoundRetryWhenLoaderReady(soundId, attempt) {
    if (!this.gameScene?.load) {
      return;
    }
    if (!this.gameScene.load.isReady()) {
      this._scheduleSoundRetry(soundId, attempt);
      return;
    }
    this._preloadSound(soundId, `?retry=${Date.now()}`);
    this.gameScene.load.start();
  }

  /**
   * ОТЛОЖЕННЫЕ ЗВУКИ (configs/deferredSounds) — звуки, без которых матч можно
   * показать и начать: голоса птиц над полем и все, что добавят в тот же
   * перечень позже.
   *
   * ЗАГРУЗКУ МАТЧА ОНИ НЕ ДЕРЖАТ. В очередь загрузки сцены они не попадают
   * вовсе: качаются отдельно, обычным запросом, и только ПОСЛЕ того, как
   * очередь сцены (картинки и основные звуки) отработала, — через паузу и по
   * одному файлу. Загрузчик сцены при этом не трогается: ни полоса загрузки,
   * ни ожидание основных звуков про эти файлы не знают. Скачанный файл
   * декодируется и кладется в кеш звуков игры — дальше он играет как обычный.
   *
   * Там, где основные звуки не грузятся (демо-партия меню с выключенным
   * звуком), не грузятся и отложенные: они пойдут следом за основными, когда
   * звук включат.
   *
   * Идемпотентно: повторный вызов ничего не делает
   */
  _scheduleDeferredSoundsLoad() {
    if (this.deferredSoundsLoadRequested || this.isShutDown) {
      return;
    }
    this.deferredSoundsLoadRequested = true;
    this.deferredSoundsTimeoutId = setTimeout(() => {
      this.deferredSoundsTimeoutId = null;
      this._loadDeferredSounds();
    }, DEFERRED_SOUNDS_LOADING.startDelayMs);
  }

  // Файлы качаются и декодируются по одному: фоновая загрузка не должна
  // спорить с самой партией ни за сеть, ни за процессор. Сбой одного файла
  // остальным не мешает; несдавшиеся звуки пробуем еще раз позже (сеть могла
  // моргнуть как раз в первые секунды партии) — до maxAttempts проходов. Что
  // не загрузилось и после них, просто не играет: playDeferredSound пропускает.
  // Звук, которого ждет эффект на поле (isAwaited: без воя нет вьюги), пробуем
  // и дальше, с долгой паузой: на слабой связи большой файл приходит не сразу
  async _loadDeferredSounds() {
    try {
      // Декодирует звук Web Audio; где его нет (или нет fetch), отложенные
      // звуки просто не играют
      if (typeof this.gameScene?.sound?.context?.decodeAudioData !== 'function' || typeof fetch !== 'function') {
        return;
      }
      const {gapMs, retryDelayMs, maxAttempts, lateRetryDelayMs, maxLateAttempts} = DEFERRED_SOUNDS_LOADING;

      for (let attempt = 1; attempt <= maxAttempts + maxLateAttempts; attempt += 1) {
        const isLate = attempt > maxAttempts;
        // Звук, привязанный к подложкам поля (вой вьюги — к зимним), на
        // прочих подложках не качается вовсе. Свои звуки отрядов фракции —
        // только для фракций, чьи отряды в этой партии нарисованы образом.
        // Звуки способностей — только если такая фракция есть в партии
        const skinFactionIds = this._getSkinFactionIds();
        const playerFactionIds = this._getPlayerFactionIds();
        const needed = Object.entries(DEFERRED_SOUNDS)
          .filter(([soundId]) => checkIfDeferredSoundNeeded(soundId, this.gameScene?.groundBackgroundId, skinFactionIds, playerFactionIds))
          .filter(([soundId]) => !this._checkIfDeferredSoundLoaded(soundId))
          // После обычных попыток — только звуки, которых ждет эффект на поле
          .filter(([, sound]) => !isLate || sound.isAwaited);
        // Свои отряды звучат с первых минут партии: автонайм крысолюдов идет уже в
        // первой фазе снабжения. Пока их файлы ждали в конце очереди, вместо своего
        // звука играл общий
        const ownFactionIds = this._getOwnFactionIds();
        const checkIfOwnSound = ([, sound]) => sound.factionIds?.some((factionId) => ownFactionIds.includes(factionId));
        const missing = [...needed.filter(checkIfOwnSound), ...needed.filter((entry) => !checkIfOwnSound(entry))];
        if (missing.length === 0) {
          return;
        }
        if (attempt > 1) {
          await this._waitDeferred(isLate ? lateRetryDelayMs : retryDelayMs);
        }
        for (const [soundId, {path, requestTimeoutMs}] of missing) {
          if (this.isShutDown) {
            return;
          }
          await this._loadDeferredSound(soundId, path, requestTimeoutMs);
          await this._waitDeferred(gapMs);
        }
      }
    } catch (error) {
      console.log(`[SoundManager.loadDeferredSounds] error: ${error}`);
    }
  }

  // Фракции, чьи отряды в этой партии нарисованы особым образом, — им и нужны
  // свои звуки отрядов (configs/factionUnitSounds). Правило то же, что у
  // загрузки картинок образов в GameScene.preload: витрина меню показывает
  // образы у всех, в остальных партиях — у игроков с открытым образом.
  // Великанам, драконам и автоматонам гремлинов образ не нужен (FACTIONS_WITH_UNIT_SOUNDS_WITHOUT_SKIN).
  // Захваченный суккубами отряд звучит по образу прежнего хозяина — его
  // фракция здесь уже есть
  _getSkinFactionIds() {
    try {
      const state = this.gameScene?.store?.getState?.();
      const players = (state && selectPlayers(state)) || [];
      return [...new Set(players
        .filter((player) => player.factionId && (
          this.gameScene?.isDemoMode
          || player.hasUnitSkin
          || FACTIONS_WITH_UNIT_SOUNDS_WITHOUT_SKIN.includes(player.factionId)
        ))
        .map((player) => player.factionId))];
    } catch (error) {
      console.log(`[SoundManager.getSkinFactionIds] error: ${error}`);
      return [];
    }
  }

  // Фракции всех игроков партии, с обликом или без него
  _getPlayerFactionIds() {
    try {
      const state = this.gameScene?.store?.getState?.();
      const players = (state && selectPlayers(state)) || [];
      return players.map((player) => player.factionId).filter(Boolean);
    } catch (error) {
      console.log(`[SoundManager.getPlayerFactionIds] error: ${error}`);
      return [];
    }
  }

  // Фракции игроков за этим экраном: своего игрока, а в локальной партии — всех живых
  _getOwnFactionIds() {
    try {
      const state = this.gameScene?.store?.getState?.();
      const ownPlayers = selectGameType(state) === GAME_TYPES.LOCAL
        ? selectPlayers(state).filter((player) => !player.isBot)
        : [getActingPlayerFromState(state)];
      return ownPlayers.map((player) => player?.factionId).filter(Boolean);
    } catch (error) {
      // Очередь просто останется в обычном порядке
      return [];
    }
  }

  // Пауза фоновой загрузки. После shutdown таймер снят и ожидание не кончается
  // никогда: загрузка на этом тихо обрывается
  _waitDeferred(delayMs) {
    return new Promise((resolve) => {
      this.deferredSoundsTimeoutId = setTimeout(resolve, delayMs);
    });
  }

  async _loadDeferredSound(soundId, path, requestTimeoutMs = DEFERRED_SOUNDS_LOADING.requestTimeoutMs) {
    const abort = typeof AbortController === 'function' ? new AbortController() : null;
    this.deferredSoundsAbort = abort;
    // Зависший запрос (связь пропала посреди файла) не должен держать очередь.
    // У большого файла предел свой: общего ему на слабой связи не хватает
    const timeoutId = setTimeout(() => abort?.abort(), requestTimeoutMs);
    try {
      const response = await fetch(path, {signal: abort?.signal});
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.arrayBuffer();
      // Сцену могли закрыть, пока файл качался
      if (this.isShutDown) {
        return;
      }
      await this._decodeDeferredSound(soundId, data);
    } catch (error) {
      if (!this.isShutDown) {
        console.log(`[SoundManager.loadDeferredSound] "${soundId}" не загружен: ${error}`);
      }
    } finally {
      clearTimeout(timeoutId);
      if (this.deferredSoundsAbort === abort) {
        this.deferredSoundsAbort = null;
      }
    }
  }

  // Декодирование — напрямую через Web Audio, а не scene.sound.decodeAudio:
  // тот по окончании пишет в кеш игры без проверок, и если игру за это время
  // разобрали (вышли из матча, повернули телефон, переподключились), из его
  // колбэка вылетает необработанная ошибка. Здесь звук кладется в кеш, только
  // пока сцена жива. Обещание завершается всегда — и при ошибке декодера тоже
  _decodeDeferredSound(soundId, data) {
    return new Promise((resolve) => {
      const handleDecoded = (audioBuffer) => {
        try {
          if (!this.isShutDown && audioBuffer) {
            this.gameScene?.cache?.audio?.add?.(soundId, audioBuffer);
          }
        } catch (error) {
          console.log(`[SoundManager.decodeDeferredSound] "${soundId}": ${error}`);
        }
        resolve();
      };
      const handleFailed = (error) => {
        if (!this.isShutDown) {
          console.log(`[SoundManager.decodeDeferredSound] "${soundId}" не декодирован: ${error}`);
        }
        resolve();
      };

      try {
        const decoding = this.gameScene.sound.context.decodeAudioData(data, handleDecoded, handleFailed);
        // Ту же ошибку браузер отдает и обещанием: она уже принята колбэком
        decoding?.catch?.(() => {});
      } catch (error) {
        handleFailed(error);
      }
    });
  }

  _checkIfDeferredSoundLoaded(soundId) {
    return Boolean(this.gameScene?.cache?.audio?.exists?.(soundId));
  }

  /**
   * Загружен ли отложенный звук — для того, что без своего звука начинаться
   * не должно (вьюга над зимним полем ждет свой вой)
   * @param {string} soundId — имя звука из DEFERRED_SOUNDS
   * @returns {boolean}
   */
  checkIfDeferredSoundLoaded(soundId) {
    try {
      return Boolean(getDeferredSound(soundId)) && this._checkIfDeferredSoundLoaded(soundId);
    } catch (error) {
      console.log(`[SoundManager.checkIfDeferredSoundLoaded] error: ${error}`);
      return false;
    }
  }

  /**
   * Отложенные звуки в этой сцене не грузятся вовсе: демо-партия меню с
   * выключенным звуком не качает и основные. Ждать их там незачем
   * @returns {boolean}
   */
  checkIfSoundOff() {
    try {
      return Boolean(this.gameScene?.isDemoMode) && this._getGlobalVolume() === 0;
    } catch (error) {
      console.log(`[SoundManager.checkIfSoundOff] error: ${error}`);
      return false;
    }
  }

  /**
   * Сыграть отложенный звук (configs/deferredSounds) — со своей громкостью из
   * перечня. Если звук еще не загружен (или не загрузился вовсе), молча
   * ничего не делает: ни ошибки, ни записи в консоль.
   *
   * Экземпляр звука независимый: уже играющие звуки он не обрывает
   *
   * @param {string} soundId — имя звука из DEFERRED_SOUNDS
   * @param {Object} [options]
   * @param {number} [options.level] — с какой доли своей громкости начать
   *   (0..1): звук, который должен нарастать плавно, начинают с нуля и
   *   поднимают через setLevel
   * @returns {{checkIfPlaying: function(): boolean, setLevel: function(number): void, stop: function(): void}|null}
   *   зазвучавший звук — им можно управлять, пока он звучит (приглушить,
   *   оборвать); null — звук не сыгран
   */
  playDeferredSound(soundId, {level = 1} = {}) {
    try {
      const deferredSound = getDeferredSound(soundId);
      if (!deferredSound || !this._checkIfDeferredSoundLoaded(soundId)) {
        return null;
      }
      const startLevel = Number.isFinite(level) ? Math.min(1, Math.max(0, level)) : 1;
      const sound = this._playOverlappingSound(soundId, deferredSound.volume * startLevel);
      if (!sound) {
        return null;
      }
      return this._createPlayingSoundControls(sound, deferredSound.volume);
    } catch (error) {
      console.log(`[SoundManager.playDeferredSound] error: ${error}`);
      return null;
    }
  }

  /**
   * Управление зазвучавшим звуком. Доигравший экземпляр уничтожает себя сам
   * (см. _playOverlappingSound), и трогать его после этого нельзя: у него уже
   * нет узлов Web Audio — поэтому каждое действие сперва сверяется, звучит ли он
   *
   * @param {Phaser.Sound.BaseSound} sound
   * @param {number} volume — своя громкость звука (множитель из перечня)
   */
  _createPlayingSoundControls(sound, volume) {
    const checkIfPlaying = () => Boolean(sound.isPlaying);
    return {
      checkIfPlaying,
      // Приглушить: level — доля (0..1) полной громкости звука. Общая
      // громкость игры читается заново при каждом вызове: долгий звук (вой
      // вьюги идет полминуты) слушается ползунка громкости, как и все прочие,
      // а не держит ту, что была на его старте
      setLevel: (level) => {
        try {
          if (checkIfPlaying() && Number.isFinite(level) && level >= 0) {
            sound.setVolume(volume * this._getGlobalVolume() * Math.min(1, level));
          }
        } catch (error) {
          console.log(`[SoundManager.setLevel] error: ${error}`);
        }
      },
      // Оборвать: уничтоженный экземпляр останавливается и убирается из игры
      stop: () => {
        try {
          sound.destroy();
        } catch (error) {
          console.log(`[SoundManager.stop] error: ${error}`);
        }
      },
    };
  }

  // Демо-партия хранит свою громкость отдельно от обычных игр (свой ключ
  // localStorage, свой дефолт 0 — выключено), чтобы слайдер в главном меню
  // не влиял на настройку звука в реальных играх и наоборот
  _getGlobalVolume() {
    const isDemo = this.gameScene.isDemoMode;
    const key = isDemo ? LOCAL_STORAGE_KEY_DEMO_VOLUME : LOCAL_STORAGE_KEY_VOLUME;
    const fallback = isDemo ? 0 : INITIAL_VOLUME;
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return fallback;
      const parsed = parseFloat(stored);
      if (isNaN(parsed)) return fallback;
      return Math.max(0, Math.min(1, parsed));
    } catch (error) {
      console.log(`[SoundManager.getGlobalVolume] error: ${error}`);
      return fallback;
    }
  }

  _getCachedSound(soundName) {
    try {
      if (!this.soundCache.has(soundName)) {
        const sound = this.gameScene.sound.add(soundName);
        this.soundCache.set(soundName, sound);
      }
      return this.soundCache.get(soundName);
    } catch (error) {
      console.log(`[SoundManager.getCachedSound] error: ${error}`);
    }
  }

  // Очистить кеш при необходимости
  _clearSoundCache() {
    try {
      this.soundCache.forEach(sound => {
        if (sound) {
          sound.stop && sound.stop();
          sound.destroy && sound.destroy();
        }
      });
      this.soundCache.clear();
    } catch (error) {
      console.log(`[SoundManager.clearSoundCache] error: ${error}`);
    }
  }

  /**
   * Отложенное продолжение звука (добивание, молотки, смех лепреконов) — через
   * часы сцены, но с защитой от протухания.
   *
   * ЗАЧЕМ. Часы Phaser двигаются дельтой кадра, а в фоновой вкладке цикл стоит:
   * отложенные события не тикают и копятся. Мгновенную часть звука гейт
   * фоновой вкладки не пускает, а вот хвосты сработали бы все разом в первую
   * же секунду после возвращения — тот же залп, только позже.
   *
   * Поэтому на срабатывании сверяем настоящее время: колбэк, пролежавший
   * заметно дольше своей задержки, относится к событию, которого игрок уже не
   * увидит, и его звук пропускается
   */
  _delaySound(delayMs, playCallback) {
    const scheduledAt = Date.now();
    this.gameScene.time.delayedCall(delayMs, () => {
      if (Date.now() - scheduledAt > delayMs + STALE_DELAYED_SOUND_TOLERANCE_MS) {
        return;
      }
      playCallback();
    });
  }

  _playSound(soundName, volume = 1) {
    try {
      if (this.gameScene.isDemoMode && this._getGlobalVolume() === 0) {
        return; // демо-партия в главном меню — звук выключен слайдером (0%)
      }
      if (this.gameScene.sound && this.gameScene.sound.locked) {
        // Sound context is locked, need user interaction first
        return;
      }
      // Вкладка в фоне (или окно без фокуса): звуки, сыгранные в спящий
      // контекст, копятся и выстреливают разом на возврате — пропускаем их
      if (checkIfSoundOutputAsleep(this.gameScene.sound)) {
        return;
      }

      const sound = this._getCachedSound(soundName);
      if (!sound) {
        return;
      }
      sound.setVolume && sound.setVolume(volume * this._getGlobalVolume());
      if (sound.isPlaying) {
        sound.stop && sound.stop();
      }
      sound.play && sound.play();
    } catch (error) {
      console.log(`[SoundManager.playSound] error: ${error}`);
    }
  }

  // Проиграть НЕЗАВИСИМЫЙ экземпляр звука, не трогая (и не обрывая) уже
  // играющие копии того же звука - в отличие от _playSound, которая держит
  // один закешированный Sound на ключ и останавливает его же перед повторным
  // проигрыванием (нужно там, где повтор должен ПЕРЕЗАПУСКАТЬ звук, а не
  // накладываться). Для этого создаётся новый Sound на каждый вызов вместо
  // переиспользования soundCache; он одноразовый - уничтожается сам по
  // 'complete', чтобы не копить в памяти сыгравшие экземпляры
  // playConfig — настройки запуска Phaser (например, {seek} — с какой секунды
  // файла начать). Возвращает зазвучавший экземпляр (не сыгран — ничего)
  _playOverlappingSound(soundName, volume = 1, playConfig = undefined) {
    try {
      if (this.gameScene.isDemoMode && this._getGlobalVolume() === 0) {
        return; // демо-партия в главном меню — звук выключен слайдером (0%)
      }
      if (this.gameScene.sound && this.gameScene.sound.locked) {
        // Sound context is locked, need user interaction first
        return;
      }
      // В фоне такой звук вдобавок не уничтожился бы: 'complete' приходит из
      // игрового цикла, а он в скрытой вкладке стоит
      if (checkIfSoundOutputAsleep(this.gameScene.sound)) {
        return;
      }

      const sound = this.gameScene.sound.add(soundName);
      if (!sound) {
        return;
      }
      sound.once('complete', () => sound.destroy && sound.destroy());
      sound.setVolume && sound.setVolume(volume * this._getGlobalVolume());
      sound.play && (playConfig ? sound.play(playConfig) : sound.play());
      return sound;
    } catch (error) {
      console.log(`[SoundManager.playOverlappingSound] error: ${error}`);
    }
  }

  // Проиграть с fade out (переиспользуя звук)
  _playSoundWithEase(soundName, fadeDuration = 2000, volume = 1) {
    if (this.gameScene.isDemoMode && this._getGlobalVolume() === 0) {
      return; // демо-партия в главном меню — звук выключен слайдером (0%)
    }
    try {
      if (this.gameScene.sound && this.gameScene.sound.locked) {
        // Sound context is locked, need user interaction first
        return;
      }
      // В фоне стоят и твины: затухание не отработает, и на возврате такой
      // звук выстрелил бы со стартовой (самой высокой) громкостью
      if (checkIfSoundOutputAsleep(this.gameScene.sound)) {
        return;
      }

      const sound = this.gameScene.sound.add(soundName);

      if (!sound) {
        return;
      }
      // Останавливаем любой текущий твин для этого звука
      const existingTweens = this.gameScene.tweens.getTweensOf(sound);
      existingTweens.forEach(tween => tween && tween.stop && tween.stop());

      // Сбрасываем громкость и перезапускаем
      const startVolume = Math.min(1, volume * this._getGlobalVolume());
      sound.setVolume && sound.setVolume(startVolume);
      if (sound.isPlaying) {
        sound.stop && sound.stop();
      }
      sound.play && sound.play();

      // Затухание задается парой from/to, а не одним to. Иначе твин берет
      // начальное значение через геттер sound.volume, а тот в Phaser 3.90
      // читает gain.value — и сразу после setValueAtTime там еще старая
      // единица (значение доезжает уже на аудиопотоке). Твин стартовал с
      // единицы, и звук играл на полной громкости мимо ползунка
      this.gameScene.tweens.add({
        targets: sound,
        volume: {from: startVolume, to: 0},
        duration: fadeDuration,
        ease: 'Cubic.easeOut',
        onComplete: () => {
          sound.stop && sound.stop();
          sound.destroy && sound.destroy();
        }
      });
    } catch (error) {
      console.log(`[SoundManager.playSoundWithEase] error: ${error}`);
    }
  }

  playMenuButtonClick() {
    this._playSound(SOUNDS.menu_button_click, 0.4);
  }

  playSideMenuClick() {
    this._playSound(SOUNDS.side_menu_click, 0.5);
  }

  playVisualToggleClick() {
    this._playSound(SOUNDS.visual_toggle_click);
  }

  playVisualTogglesExpand() {
    this._playSound(SOUNDS.visual_toggles_expand, 0.5);
  }

  playInitiativeInterception() {
    this._playSound(SOUNDS.initiative);
  }

  playUndoClick() {
    this._playSound(SOUNDS.undo_click);
  }

  // Sound methods
  playGameStart() {
    if (!isEnvConfEnabled(ENV_CONFIG_VARIABLES.REACT_APP_NO_SOUND_START_GAME)) {
      this._playSound(SOUNDS.game_start, 0.5);
    }
  }

  playPhaseSupply() {
    this._playSound(SOUNDS.phase_supply);
  }

  playPhaseConstruction() {
    this._playSound(SOUNDS.phase_construction);
  }

  playPhaseWar() {
    this._playSound(SOUNDS.phase_war);
  }

  playUnitRecruitment(unitType, ownerId, currentPlayer, price, unit = null) {
    this.playUnitSelect(unitType, ownerId, currentPlayer, unit);
    this.playCoins(price);
  }

  /** Волшебный подъем под разгон анимации найма: у всех, кроме простой пехоты d6 */
  playUnitRecruitmentCast(unitType) {
    const {castSoundLevel} = resolveUnitRecruitmentCast(unitType);
    if (castSoundLevel > 0) {
      this.playDeferredSound('ability_unit_upgrade_magic', {level: castSoundLevel});
    }
  }

  /** Удар в момент проявления отряда: только у самых старших типов */
  playUnitRecruitmentImpact(unitType) {
    const {impactSoundLevel} = resolveUnitRecruitmentCast(unitType);
    if (impactSoundLevel > 0) {
      this.playDeferredSound('ability_goblins_bomb_explosion', {level: impactSoundLevel});
    }
  }

  playUnitUpgrade(unitType, ownerId, currentPlayer, price, unit = null) {
    this._playSound(SOUNDS.unit_upgrade, 0.5);
    // Волшебный подъем под золотые шевроны — у всех фракций (configs/deferredSounds)
    this.playDeferredSound('ability_unit_upgrade_magic');
    this._delaySound(500, () => {
      this.playUnitRecruitment(unitType, ownerId, currentPlayer, price, unit && {...unit, type: unitType});
    });
  }

  playEnemyUnit() {
    this._playSound(SOUNDS.select_unit_enemy);
  }

  /**
   * Фракция, чей СВОЙ звук положен отряду (configs/factionUnitSounds), — или
   * null, если отряд звучит общим звуком.
   *
   * Правило то же, что у картинки отряда на поле (checkIfUnitHasFactionSkin):
   * свой звук — у отряда, нарисованного особым образом фракции. Отряда еще нет
   * (выбор типа при улучшении) — решает игрок: открыт ли у него образ. Великану
   * дракону и автоматону образ не нужен (checkIfUnitSoundsWithoutSkin), а нейтральный отряд
   * и у таких фракций звучит общим звуком: он нарисован своей иконкой
   *
   * @param {Object|null} unit
   * @param {Object|null} ownerPlayer
   * @param {string} unitType
   * @returns {string|null}
   */
  _resolveUnitSoundFactionId(unit, ownerPlayer, unitType) {
    const isEverySkinShown = Boolean(this.gameScene?.isDemoMode);
    if (unit) {
      const state = this.gameScene?.store?.getState?.();
      const players = state ? selectPlayers(state) : [];
      if (checkIfUnitHasFactionSkin(unit, players || [], {isEverySkinShown})) {
        return unit.factionId;
      }
      const isNeutralUnit = (unit.capturedFromOwnerId ?? unit.ownerId) === NEUTRAL_PLAYER_ID;
      return !isNeutralUnit && checkIfUnitSoundsWithoutSkin(unit.factionId, unitType) ? unit.factionId : null;
    }
    if (ownerPlayer?.factionId && (
      isEverySkinShown
      || ownerPlayer.hasUnitSkin
      || checkIfUnitSoundsWithoutSkin(ownerPlayer.factionId, unitType)
    )) {
      return ownerPlayer.factionId;
    }
    return null;
  }

  /**
   * Свой звук отряда фракции для события kind — имя загруженного отложенного
   * звука или null (своего звука нет или он еще не загрузился: тогда звучит
   * общий)
   */
  _getFactionUnitSoundId(kind, unitType, unit, ownerPlayer) {
    const factionId = this._resolveUnitSoundFactionId(unit, ownerPlayer, unitType);
    const soundId = resolveFactionUnitSoundId({factionId, unitType, kind, hasFactionSkin: Boolean(factionId)});
    return soundId && this._checkIfDeferredSoundLoaded(soundId) ? soundId : null;
  }

  // Отряд соперника звучит так же, как свой: в облике фракции — своим звуком, без
  // него — общим звуком типа. Общий звук чужого остался у нейтральных отрядов (у
  // них нет ни игрока, ни облика) и у чужого вызова без самого отряда: без него
  // облик определить не по чему
  playUnitSelect(unitType, ownerId, currentPlayer, unit = null) {
    if (ownerId !== currentPlayer?.id && (!unit || ownerId === NEUTRAL_PLAYER_ID)) {
      this.playEnemyUnit();
      return;
    }

    try {
      const factionSoundId = this._getFactionUnitSoundId('select', unitType, unit, currentPlayer);
      if (factionSoundId) {
        this._playSound(factionSoundId, getDeferredSound(factionSoundId).volume);
        return;
      }
    } catch (error) {
      console.log(`[SoundManager.playUnitSelect] error: ${error}`);
    }

    switch (unitType) {
      case UNIT_TYPES.D6.id:
        this._playSound(SOUNDS.select_unit_d6)
        break;
      case UNIT_TYPES.D10.id:
        this._playSound(SOUNDS.select_unit_d10)
        break;
      case UNIT_TYPES.D20.id:
        this._playSound(SOUNDS.select_unit_d20)
        break;
      case UNIT_TYPES.D8.id:
        this._playSound(SOUNDS.select_unit_d8)
        break;
      case UNIT_TYPES.D12.id:
        this._playSound(SOUNDS.select_unit_d12)
        break;
      case UNIT_TYPES.D4.id:
        this._playSound(SOUNDS.select_unit_d4)
        break;
      default:
        this._playSound(SOUNDS.select_unit_d6)
    }
  }

  playUnitMovement(unitType, unit = null) {
    try {
      const factionSoundId = this._getFactionUnitSoundId('move', unitType, unit, null);
      if (factionSoundId) {
        // Как и общий звук хода — с затуханием: отряд может еще лететь, а
        // звук не должен обрываться на полуслове
        this._playSoundWithEase(factionSoundId, 3000, 1.5 * getDeferredSound(factionSoundId).volume);
        return;
      }
    } catch (error) {
      console.log(`[SoundManager.playUnitMovement] error: ${error}`);
    }

    let soundId;

    switch (unitType) {
      case UNIT_TYPES.D6.id:
        soundId = SOUNDS.move_d6;
        break;
      case UNIT_TYPES.D10.id:
        soundId = SOUNDS.move_d10;
        break;
      case UNIT_TYPES.D20.id:
        soundId = SOUNDS.move_d20;
        break;
      case UNIT_TYPES.D8.id:
        soundId = SOUNDS.move_d8;
        break;
      case UNIT_TYPES.D12.id:
        soundId = SOUNDS.move_d12;
        break;
      case UNIT_TYPES.D4.id:
        soundId = SOUNDS.move_d4;
        break;
      default:
        soundId = SOUNDS.move_d6;
    }

    this._playSoundWithEase(soundId, 3000, 1.5)
  }

  playModalCombatResults() {
    this._playSound(SOUNDS.agony_1, 0.7);
    this._delaySound(1200, () => {
      this._playSound(SOUNDS.agony_2);
    });
  }

  /** Агония отряда, погибшего от голода. Своего файла еще нет — звучит общая агония */
  playUnitStarvationAgony() {
    if (!this.playDeferredSound('unit_starvation_agony')) {
      this._playSound(SOUNDS.agony_2);
    }
  }

  playModalCityCapture() {
    this._playSound(SOUNDS.modal_city_capture);
  }

  playModalRoundEnd() {
    this._playSound(SOUNDS.modal_round_end);
  }

  playToastInfo() {
    this._playSound(SOUNDS.alert_info);
  }

  playToastError() {
    this._playSound(SOUNDS.alert_error);
  }

  playTurnEnd() {
    this._playSound(SOUNDS.turn_end, 0.7)
  }

  playWarStageEnd() {
    this._playSound(SOUNDS.turn_stage_end)
  }

  playStartingQuarterPlacement() {
    this._playSound(SOUNDS.starting_quarter, 0.5)
  }

  /** Чужой стартовый квартал раскрыт: удар как у центрального, рог — один на все раскрытие */
  playStartingQuarterReveal({isHornPlayed}) {
    this._playSound(SOUNDS.build_central_quarter, 0.5)
    if (isHornPlayed) {
      this.playDeferredSound(CITY_FOUNDED_SOUND_ID);
    }
  }

  playSelectFarm() {
    this._playSound(getRandomElement([
      SOUNDS.select_farm_1, SOUNDS.select_farm_2, SOUNDS.select_farm_3, SOUNDS.select_farm_4, SOUNDS.select_farm_5, SOUNDS.select_farm_6, SOUNDS.select_farm_7
    ]))
  }

  playDistributeSupply() {
    this._playSound(SOUNDS.distribute_supply)
  }

  playBuildingRepair() {
    this._playSound(SOUNDS.build_common)
  }

  playBuildingPlacement(token, price) {
    this.playCoins(price)

    if (checkIfCentralQuarter(token)) {
      this._playSound(SOUNDS.build_central_quarter, 0.5)
      // Основание города: поверх удара трубит боевой рог
      this.playDeferredSound(CITY_FOUNDED_SOUND_ID);
      return;
    }

    switch (token) {
      case KEY_POINT_TOKENS.MINE:
      case KEY_POINT_TOKENS.SUPER_MINE:
        this.playDwarvesMineExploration(token)
        break;
      case COMMON_BUILDING_TOKENS.ROAD:
        this._playSound(SOUNDS.build_road)
        break;
      case COMMON_BUILDING_TOKENS.BARRACKS:
      case COMMON_BUILDING_TOKENS.FARM:
      case COMMON_BUILDING_TOKENS.QUARTER:
        this._playSound(SOUNDS.build_common)
        break;
      // case SPECIAL_BUILDING_TOKENS.FORTRESS:
      //   this._playSound(SOUNDS.build_fortress)
      //   break;
      default:
        this._playSound(SOUNDS.build_road);
        this._delaySound(100, () => {
          this._playSoundWithEase(SOUNDS.build_hammers, 1400)
        });
        this._delaySound(200, () => {
          this._playSoundWithEase(SOUNDS.build_special, 1500, 0.3)
        });
        if (checkIfSpecialBuilding(token)) {
          this._delaySound(1100, () => {
            this.playSpecialBuildingPlacement(token);
          });
        }
    }
  }

  playSpecialBuildingPlacement(token) {
    const volume = 1;

    switch (token) {
      case SPECIAL_BUILDING_TOKENS.WAREHOUSE:
        this._playSound(SOUNDS.special_building_warehouse_1, volume)
        // this.gameScene.time.delayedCall(200, () => {
        //   this._playSound(SOUNDS.special_building_warehouse_2,     pointer-events: all;volume)
        // });
        break
      case SPECIAL_BUILDING_TOKENS.PALACE:
        this._playSound(SOUNDS.special_building_palace, volume)
        break;
      case SPECIAL_BUILDING_TOKENS.MILITARY_CAMP:
        this._playSound(SOUNDS.special_building_military_camp, 0.8)
        break;
      case SPECIAL_BUILDING_TOKENS.ARSENAL:
        this._playSound(SOUNDS.special_building_arsenal, volume)
        break;
      case SPECIAL_BUILDING_TOKENS.TEMPLE:
        this.playTemple();
        break;
      case SPECIAL_BUILDING_TOKENS.GUILD:
        this.playGuild();
        break;
      case SPECIAL_BUILDING_TOKENS.FORTRESS:
        this._playSound(SOUNDS.build_fortress, 1.5)
        break;
      default:
    }
  }

  playGuild() {
    this._playSound(SOUNDS.special_building_guild)
  }

  playTemple() {
    this._playSound(SOUNDS.special_building_temple)
  }

  // Город получил рудник или руины — звук идет вместе с анимацией их дохода
  // (см. playCityGrowthAnimation), как у дохода гильдий и храмов. Рудник
  // звучит так же, как его разведка гномами. Файлы руин записаны громче
  // звука храма (−11.5 LUFS против −14.7): громкость их выравнивает
  playKeyPointCapture(token) {
    switch (token) {
      case KEY_POINT_TOKENS.MINE:
      case KEY_POINT_TOKENS.SUPER_MINE:
        this.playDwarvesMineExploration(token);
        break;
      case KEY_POINT_TOKENS.RUINS:
        this._playSound(SOUNDS.ruins_capture, 0.7);
        break;
      case KEY_POINT_TOKENS.SUPER_RUINS:
        this._playSound(SOUNDS.super_ruins_capture, 1);
        break;
      default:
    }
  }

  playTokenClick(token) {
    if (checkIfAnyBuildingToken(token)) {
      this._playSound(SOUNDS.token_click_page);
      return;
    }

    if (checkIfCentralQuarter(token)) {
      this._playSound(SOUNDS.token_click_common, 0.6);
      return;
    }

    switch (token) {
      case COMMON_BUILDING_TOKENS.ROAD:
      case COMMON_BUILDING_TOKENS.BARRACKS:
      case COMMON_BUILDING_TOKENS.FARM:
      case COMMON_BUILDING_TOKENS.QUARTER:
      case MINE_EXPLORATION:
        this._playSound(SOUNDS.token_click_common, 0.6);
        break;
      default:
        this._playSound(SOUNDS.token_click_special, 0.4);
    }
  }

  playUnitRecruitmentTypeSelect() {
    this._playSound(SOUNDS.token_click_unit, 0.4);
  }

  // Клик по ячейке с обычным жетоном (без специального строения) при
  // просмотре информации о ячейке во время чужого хода
  playTokenClickCommon() {
    this._playSound(SOUNDS.token_click_common, 0.6);
  }

  /**
   * Звук выстрела: пуск сразу, итог (удар, крик цели, сигнал неудачи) — с
   * задержкой, под анимацию залпа на поле (числа согласованы с
   * configs/shotAnimation: валун летит около секунды).
   *
   * timeFactor — во сколько раз залп на поле сжат по времени под шаг темпа
   * чужих действий (см. resolveShotTimeFactor): сжатый залп попадает в цель
   * раньше, и итог должен прозвучать тогда же, а не после вспышки удара
   */
  playShooting(isUnitMeleeElf, isUnitTarget, isBadLuck, timeFactor = 1) {
    const delayFactor = Number.isFinite(timeFactor) && timeFactor > 0 && timeFactor <= 1 ? timeFactor : 1;
    const scaleDelay = (delayMs) => Math.round(delayMs * delayFactor);

    if (isUnitMeleeElf) {
      this._playSound(SOUNDS.elves_shooting);
    } else {
      this._playSound(SOUNDS.shooting_d4);
    }

    if (isBadLuck) {
      this._delaySound(scaleDelay(1000), () => {
        this.playRerollBadLuck()
      });
    } else if (isUnitMeleeElf) {
      this._delaySound(scaleDelay(300), () => {
        this._playSound(SOUNDS.elves_shooting_agony, 0.7)
      });
    } else {
      if (isUnitTarget) {
        this._delaySound(scaleDelay(700), () => {
          this._playSound(SOUNDS.shooting_impact_cry_1, 0.7)
          this._delaySound(scaleDelay(300), () => {
            this._playSound(SOUNDS.shooting_d4_impact, 0.5)
          });
        });
      } else {
        this._delaySound(scaleDelay(1000), () => {
          this._playSound(SOUNDS.shooting_d4_impact, 0.5)
        });
      }
    }
  }

  playShootingD4DiceSelect() {
    this._playSoundWithEase(SOUNDS.select_dice_shooting_d4, 1000);
  }

  playMeleeDiceSelect() {
    this._playSound(SOUNDS.select_dice_melee);
  }

  // Флаги выбранных костей по порядку (у селестиалов в ближнем бою их 4):
  // одна кость — звук одиночного броска, несколько — звук пары
  playRerollDices(...diceSelectedFlags) {
    if (diceSelectedFlags.filter(Boolean).length > 1) {
      this._playSound(getRandomElement([SOUNDS.dice_2_1, SOUNDS.dice_2_2, SOUNDS.dice_2_3, SOUNDS.dice_2_4]))
    } else {
      this._playSound(getRandomElement([SOUNDS.dice_1_1, SOUNDS.dice_1_2, SOUNDS.dice_1_3, SOUNDS.dice_1_4]))
    }
  }

  playRerollBadLuck() {
    this._playSound(SOUNDS.reroll_bad_luck);
  }

  playMeleeCombat() {
    this._playSound(SOUNDS.battle_1);
    this._delaySound(1000, () => {
      this._playSound(SOUNDS.battle_2, 0.7);
    });
  }

  playMeleeCombatAfterReroll() {
    this._playSound(SOUNDS.battle_2, 0.7);
  }

  /**
   * Взмахи оружия в анимации ближнего боя: звучат в момент, когда удары
   * начинаются (см. HexGridRenderer.showMeleeAnimation). Звук — по урону
   * удара. Удары одной волны начинаются разом: одинаковые звуки сливаются в
   * один, разные звучат вместе.
   *
   * Каждый звук — своим экземпляром: волны идут чаще, чем звук успевает
   * отзвучать, и общий экземпляр обрывался бы следующей волной той же
   * ступени (в сжатом чужом бою — еще до своей громкой части). Тихое
   * вступление файла пропускается (см. MELEE_STRIKE_SOUNDS)
   *
   * @param {number[]} damages — урон ударов, начавшихся в этот момент
   */
  playMeleeStrikes(damages) {
    const tiers = new Set((damages || []).map(resolveMeleeStrikeSoundTier).filter(Boolean));
    tiers.forEach(({sound, skipMs}) => {
      this._playOverlappingSound(sound, 1, skipMs > 0 ? {seek: skipMs / 1000} : undefined);
    });
  }

  playPriorityTargetSelect() {
    this._playSound(SOUNDS.priority_target_select);
  }

  playCoins(coinsCount) {
    if (coinsCount === 0) {
      return;
    }

    let soundId;
    if (coinsCount === 1) {
      soundId = getRandomElement([SOUNDS.coins_1_1, SOUNDS.coins_1_2, SOUNDS.coins_1_3])
    } else if (coinsCount === 2) {
      soundId = getRandomElement([SOUNDS.coins_2_1, SOUNDS.coins_2_2, SOUNDS.coins_2_3])
    } else if (coinsCount === 3) {
      soundId = getRandomElement([SOUNDS.coins_3_1, SOUNDS.coins_3_2, SOUNDS.coins_3_3])
    } else if (coinsCount >= 4 && coinsCount < 8) {
      soundId = getRandomElement([SOUNDS.coins_some_1, SOUNDS.coins_some_2, SOUNDS.coins_some_3])
    } else {
      soundId = getRandomElement([SOUNDS.coins_many_1, SOUNDS.coins_many_2, SOUNDS.coins_many_3])
    }

    this._playSound(soundId);
  }

  playCardOpen() {
    this._playSound(SOUNDS.card_open);
  }

  playBuyPolicy(price) {
    this.playCoins(price)
    this._playSound(SOUNDS.policy_buy, 0.7);
  }

  playClearPolicyMarket() {
    this._playSound(SOUNDS.policy_market_clear);
  }

  playBirds() {
    if (this.birdsSound?.isPlaying) return;

    this.birdsSound = this.gameScene.sound.add('birds');
    this.birdsSound.play({loop: true, volume: this._getGlobalVolume()});
  }

  updateBirdsVolume() {
    if (!this.birdsSound?.isPlaying) return;

    this.birdsSound.setVolume(this._getGlobalVolume());
  }

  stopBirds() {
    if (this.birdsSound) {
      this.birdsSound.stop();
      this.birdsSound.destroy();
      this.birdsSound = null;
    }
  }

  playTimer() {
    if (this.timerSound?.isPlaying) return;

    this.timerSound = this.gameScene.sound.add(SOUNDS.timer);
    this.timerSound.play({loop: false, volume: 0.5 * this._getGlobalVolume()});
  }

  stopTimer() {
    if (this.timerSound) {
      this.timerSound.stop();
      this.timerSound.destroy();
      this.timerSound = null;
    }
  }

  // FACTIONS
  // Шар размера летит от квартала к отряду: волшебное мерцание, тихое —
  // докупок за ход много
  playUnitSizeTransfer() {
    this.playDeferredSound('ability_leprechauns_theft_magic', {level: 0.6});
  }

  // Звон монет, следом смех самого отряда-вора; пока его файл не загружен —
  // общий смех фракции
  playLeprechaunsTheft({unitType} = {}) {
    this._playSound(SOUNDS.leprechauns_coins);
    this._delaySound(700, () => {
      if (!this.playDeferredSound(resolveLeprechaunsTheftLaughSoundId(unitType))) {
        this._playSound(SOUNDS.leprechauns_laugh, 0.7);
      }
    });
  }

  // Способности суккубов и гоблинов (см. scenes/playAbilityCastEffects):
  // заклинание звучит с вылетом сердца, бомбы или блесток, итог — в момент
  // попадания. Свои звуки способностей отложенные (configs/deferredSounds):
  // пока файл не загружен, итог звучит прежним общим звуком, а заклинание
  // молчит

  playSuccubesSeductionCast() {
    this.playDeferredSound('ability_succubes_charm_cast');
  }

  playSuccubesSeductionSuccess() {
    // Рык демона и шепот — поверх успеха, а не вместо него
    this.playDeferredSound('ability_succubes_charm_demon');
    if (!this.playDeferredSound('ability_succubes_charm_success')) {
      MENU_SOUND_MANAGER.playFactionSelect(FACTIONS.SUCCUBES, true)
    }
  }

  playSuccubesSeductionFailure() {
    if (!this.playDeferredSound('ability_succubes_charm_failure')) {
      this._playSound(SOUNDS.reroll_bad_luck);
    }
  }

  playGoblinsTrapCast() {
    this.playDeferredSound('ability_goblins_trap_cast');
  }

  /**
   * Капкан щелкает, гоблин смеется — своим смехом отряда
   * @param {Object} [options]
   * @param {string} [options.unitType] — тип отряда
   */
  playGoblinsTrapSetupSuccess({unitType} = {}) {
    this._playSound(SOUNDS.trap_set);
    this._delaySound(250, () => this.playDeferredSound(resolveGoblinsSuccessSoundId(unitType)));
  }

  playGoblinsTrapSetupFailure() {
    this._playSound(SOUNDS.reroll_bad_luck);
  }

  playGoblinsTrapRemoval() {
    this._playSound(SOUNDS.coins_1_1);
  }

  playGoblinsBombThrow() {
    this.playDeferredSound('ability_goblins_bomb_throw');
  }

  /**
   * Взрыв бомбы у строения (разрушенное — еще и грохот попадания валуна
   * следом), а когда он отгремит — смех гоблина, своим смехом отряда
   * @param {Object} [options]
   * @param {boolean} [options.destroyed]
   * @param {string} [options.unitType] — тип отряда
   */
  playGoblinsBuildingDamageSuccess({destroyed = false, unitType} = {}) {
    if (!this.playDeferredSound('ability_goblins_bomb_explosion')) {
      this._playSound(SOUNDS.shooting_d4_impact);
    } else if (destroyed) {
      this._delaySound(GOBLINS_DESTROYED_SECOND_BLAST_DELAY_MS, () => this._playSound(SOUNDS.shooting_d4_impact, 0.8));
    }
    this._delaySound(GOBLINS_LAUGH_AFTER_EXPLOSION_DELAY_MS, () => this.playDeferredSound(resolveGoblinsSuccessSoundId(unitType)));
  }

  playGoblinsBuildingDamageFailure() {
    this._playSound(SOUNDS.reroll_bad_luck);
  }

  /**
   * Один звук на КАЖДУЮ сработавшую ловушку за это перемещение - юнит мог
   * задеть несколько ловушек за один проход (в т.ч. нескольких разных
   * игроков на одном гексе, см. resolveMovementWithTraps.triggeredTrapEvents/
   * trapsByOwner). Со сдвигом TRAP_TRIGGER_SOUND_STAGGER_MS между ними -
   * иначе при нескольких одновременных срабатываниях звуки наложились бы
   * друг на друга неразличимой кашей (пожелание пользователя)
   * @param {{trapsByOwner: {ownerId: string, damage: number}[]}[]} triggeredTrapEvents
   */
  playGoblinsTrapTrigger(triggeredTrapEvents) {
    const trapCount = triggeredTrapEvents.reduce((sum, event) => sum + event.trapsByOwner.length, 0);

    for (let i = 0; i < trapCount; i++) {
      this._delaySound(i * TRAP_TRIGGER_SOUND_STAGGER_MS, () => {
        // _playOverlappingSound, а не _playSound - иначе каждый следующий
        // звук обрывал бы предыдущий (тот держит один переиспользуемый Sound
        // на ключ и глушит его перед повтором), и до конца доигрывал бы
        // только последний (замечание пользователя)
        this._playOverlappingSound(SOUNDS.trap_trigger);
      });
    }
  }

  playDwarvesMineExploration(token) {
    this._playSound(SOUNDS.dwarves_mine_exploration_1);
    if (token === KEY_POINT_TOKENS.SUPER_MINE) {
      this._playSound(SOUNDS.dwarves_mine_exploration_2);
    }
  }
}